import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RefreshCw, ArrowLeft } from 'lucide-react';
import type { RootState } from '@/state/store';
import { useSelectedComponent } from '@/hooks/useSelectedComponent';
import ComponentNotFound from '@/hooks/ComponentNotFound';
import OnboardingPageHeader from '@/components/smartai/ComponentOnboarding/OnboardingPageHeader';
import OnboardingProjectHeader from '@/components/smartai/ComponentOnboarding/OnboardingProjectHeader';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { CoverageHistoryMode } from '@/components/smartai/api/getCoverageHistory';
import { useCoverageHistoryTrendBranch } from '@/components/smartai/api/getCoverageHistory';
import {
  compareFromTrendPoints,
  HISTORY_MODE_LABELS,
  type CoverageChartMetric,
} from './coverage-history-utils';
import {
  coverageCardClass,
  coveragePageClass,
  CoverageStateMessage,
} from './coverage-dashboard-layout';
import {
  TrendSection,
  TrendSegmentGroup,
} from './coverage-trend-layout';
import CoverageTrendDashboardMetrics from './CoverageTrendDashboardMetrics';
import CoverageByBuildChart from './CoverageByBuildChart';
import CoverageTrendChart from './CoverageTrendChart';
import CoverageHistoryRunsTable from './CoverageHistoryRunsTable';
import CoverageDashboardSkeleton from './CoverageDashboardSkeleton';

const MODE_OPTIONS: CoverageHistoryMode[] = [
  'combined',
  'automation',
  'manual',
];

function parseMode(raw: string | null): CoverageHistoryMode {
  if (raw === 'automation' || raw === 'manual' || raw === 'combined') {
    return raw;
  }
  return 'combined';
}

const ComponentCoverageHistoryView = () => {
  const { ready, notFound, isLoading, dbRepoId } = useSelectedComponent();
  const selected = useSelector(
    (state: RootState) => state.userSelectedComponentSlice,
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const [chartMetric, setChartMetric] = useState<CoverageChartMetric>('both');

  const mode = parseMode(searchParams.get('bucket'));

  const setMode = (next: CoverageHistoryMode) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('bucket', next);
    setSearchParams(nextParams, { replace: true });
  };

  const repoId = selected.dbRepoId ?? dbRepoId ?? '';
  const branchId = selected.branchId ?? '';

  const branchQuery = useCoverageHistoryTrendBranch(repoId, branchId, mode);
  const points = branchQuery.data ?? [];

  const compare = useMemo(() => compareFromTrendPoints(points), [points]);

  const handleRefresh = () => {
    void branchQuery.refetch();
  };

  if (notFound) {
    return <ComponentNotFound />;
  }

  if (isLoading || !ready) {
    return (
      <div className={coveragePageClass}>
        <CoverageDashboardSkeleton />
      </div>
    );
  }

  return (
    <div className={coveragePageClass}>
      <OnboardingPageHeader
        eyebrow="Coverage Analysis"
        title="Coverage trend"
        subtitle="Build trend — one point per code scan on this branch (stored scan coverage)."
      />

      <OnboardingProjectHeader
        org={selected.orgName}
        project={selected.projectName || '—'}
        repoName={selected.repoName || '—'}
        branchName={
          (selected.branchName || 'main').replace(/^refs\/heads\//, '') || '—'
        }
      />

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" size="sm" asChild className="h-8 gap-1.5 rounded-lg">
          <Link to={`/sia/components/coverage_analysis/dashboard/${repoId}`}>
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            Live dashboard
          </Link>
        </Button>
        {branchId ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 rounded-lg"
            onClick={handleRefresh}
            disabled={branchQuery.isFetching}>
            <RefreshCw
              className={cn(
                'h-3.5 w-3.5',
                branchQuery.isFetching && 'animate-spin',
              )}
              aria-hidden
            />
            Refresh
          </Button>
        ) : null}
      </div>

      <section className={cn(coverageCardClass, 'space-y-6')}>
        <div className="flex flex-col gap-4 border-b border-[var(--inv-border-soft)] pb-5 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div className="min-w-0 max-w-xl">
            <h3 className="m-0 font-serif text-[1rem] font-semibold text-[var(--inv-text)]">
              Branch build trend
            </h3>
            <p className="m-0 mt-1 text-[0.8125rem] leading-relaxed text-[var(--inv-text-secondary)]">
              One data point per code scan on this branch. Compare coverage across
              builds and switch how metrics are measured.
            </p>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:gap-6">
            <TrendSegmentGroup
              label="Coverage source"
              options={MODE_OPTIONS.map(m => ({
                value: m,
                label: HISTORY_MODE_LABELS[m],
              }))}
              value={mode}
              onChange={setMode}
            />
            <TrendSegmentGroup
              label="LOC / inventory"
              ariaLabel="Lines of code or inventory measure"
              options={[
                { value: 'both', label: 'Both' },
                { value: 'lines', label: 'Lines' },
                { value: 'inventory', label: 'Inventory' },
              ]}
              value={chartMetric}
              onChange={setChartMetric}
            />
          </div>
        </div>

        {!branchId ? (
          <CoverageStateMessage variant="empty">
            Branch id is missing for this component; build trend is unavailable.
          </CoverageStateMessage>
        ) : null}

        {branchQuery.isPending && branchId ? (
          <CoverageDashboardSkeleton />
        ) : null}

        {branchQuery.isError ? (
          <CoverageStateMessage variant="error">
            Failed to load trend: {(branchQuery.error as Error)?.message}
          </CoverageStateMessage>
        ) : null}

        {branchId && !branchQuery.isPending && !branchQuery.isError ? (
          <div className="space-y-8">
            <TrendSection
              title="Latest build snapshot"
              description={
                compare.latest?.scanNumber != null
                  ? `Scan #${compare.latest.scanNumber} — deltas vs. the previous build on this branch.`
                  : 'Deltas compare the latest scan to the previous build on this branch.'
              }>
              <CoverageTrendDashboardMetrics
                mode={mode}
                metricKind={chartMetric}
                latest={compare.latest}
                previous={compare.previous}
              />
            </TrendSection>

            <CoverageByBuildChart points={points} metric={chartMetric} />

            <TrendSection
              title="Coverage over time"
              description="Trend line per scan; follows LOC / inventory above (both shows two series).">
              <CoverageTrendChart points={points} metric={chartMetric} />
            </TrendSection>

            <TrendSection
              title="Build history"
              description="Newest builds first. Click a row to view coverage runs within that build.">
              {points.length ? (
                <CoverageHistoryRunsTable
                  points={points}
                  rowHref={row => {
                    const id = row.scanId?.trim();
                    if (!id || !repoId) return undefined;
                    return `/sia/components/coverage_analysis/trend/${repoId}/build/${encodeURIComponent(id)}?bucket=${mode}`;
                  }}
                />
              ) : (
                <CoverageStateMessage variant="empty">
                  No scans with coverage on this branch yet.
                </CoverageStateMessage>
              )}
            </TrendSection>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default ComponentCoverageHistoryView;
